// **Eine Meldung** (`/feedback/<id>`): Text, Anhänge, Kontext, Verlauf und die
// Antwort — Kommentar und Statuswechsel. Anhänge immer als Download: Der
// Inhalt kommt von einem Kunden und wird nie im Ursprung der Plattform
// angezeigt.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { get, patch, post } from "../../api/client";
import { keys } from "../../api/queries";
import type { FeedbackReport } from "../../api/types";
import { PageHead } from "../../app/page-head";
import { fmtDate } from "../../lib/format";
import { Loading, Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import { toast } from "../../ui/toaster";
import { Lines, StatusBadge, TypeBadge } from "./badges";
import { STATUS, feedbackFail, nextStatuses, statusBlocked } from "./feedback-model";

const BACK = ["/feedback", "Feedback"] as const;

/** Die Felder des Kontexts, die der Client der Instanz mitgeschickt hat — leere entfallen. */
export function contextRows(ctx: NonNullable<FeedbackReport["context"]>): Array<[string, string]> {
  const rows: Array<[string, string | undefined]> = [
    ["Seite", ctx.page], ["Browser", ctx.browser], ["Fenster", ctx.viewport],
    ["Version", [ctx.app_version, ctx.app_build].filter(Boolean).join(" · ")], ["Umgebung", ctx.environment],
    ["Sprache / Theme", [ctx.locale, ctx.theme].filter(Boolean).join(" · ")], ["Agent", ctx.agent_name],
    ["Integrationen", (ctx.integrations ?? []).join(", ")],
  ];
  return rows.filter((r): r is [string, string] => Boolean(r[1]));
}

export function FeedbackReportView({ id }: { id: number }) {
  const query = useQuery({ queryKey: keys.feedbackReport(id), queryFn: () => get<FeedbackReport>(`/feedback/${id}`) });
  if (query.isError) {
    return (
      <>
        <PageHead title="Feedback" back={BACK} />
        <Card title="Meldung"><Note tone="error">{feedbackFail(query.error)}</Note></Card>
      </>
    );
  }
  if (!query.data) return <Loading />;
  return <Report id={id} r={query.data} />;
}

function Report({ id, r }: { id: number; r: FeedbackReport }) {
  const client = useQueryClient();
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const ctx = r.context ?? null;

  const comment = async () => {
    if (!text.trim()) { setError("Bitte einen Text eingeben."); return; }
    setBusy(true);
    try {
      await post(`/feedback/${id}/comments`, { body: text.trim() });
      toast("Kommentar gesendet");
      client.setQueryData(keys.feedbackReport(id), await get<FeedbackReport>(`/feedback/${id}`));
      setText("");
      setError("");
    } catch (e) { setError(feedbackFail(e)); } finally { setBusy(false); }
  };

  const setStatus = async (status: string) => {
    const blocked = statusBlocked(status, text);
    if (blocked) { setError(blocked); return; }
    setBusy(true);
    try {
      const next = await patch<FeedbackReport>(`/feedback/${id}`, { status, note: text.trim() || undefined });
      client.setQueryData(keys.feedbackReport(id), next);
      toast(`Status: ${STATUS[status]?.[0] ?? status}`);
      setText("");
      setError("");
    } catch (e) { setError(feedbackFail(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <PageHead
        title={r.title}
        hint={<><TypeBadge type={r.type} /> <StatusBadge status={r.status} /> · {r.tenant?.name ?? ""} · {fmtDate(r.created_at)}</>}
        back={BACK}
      />
      <Card title={r.author?.name || r.author?.email || "Unbekannt"} hint={r.author?.email ?? ""}>
        <div className="pf-prose"><Lines text={r.body} /></div>
        {r.attachments?.length ? (
          <div className="pf-row pf-attachments">
            {r.attachments.map((a) => <a key={a.id} className="pf-btn pf-btn--sm" href={`/api/feedback/attachments/${a.id}`} download>{a.name}</a>)}
          </div>
        ) : null}
        {r.resolution_note ? <Note>Begründung: {r.resolution_note}</Note> : null}
      </Card>
      {ctx ? (
        <Card title="Kontext">
          <div className="pf-grid">
            {contextRows(ctx).map(([l, v]) => (
              <div key={l} className="pf-field"><span className="pf-field__label">{l}</span><span className="pf-field__value">{v}</span></div>
            ))}
          </div>
        </Card>
      ) : null}
      <Card title="Verlauf" flush>
        {(r.comments ?? []).length ? (
          <ul className="pf-feed">
            {(r.comments ?? []).map((c, i) => (
              <li key={i} className="pf-feed__item pf-feed__item--static">
                <span className="pf-feed__main">
                  <strong>{c.author?.name || c.author?.email || ""}</strong>
                  {c.status_change ? <> <span className="pf-sub">setzt <StatusBadge status={c.status_change.split(":")[1] ?? c.status_change} /></span></> : null}
                  {c.body ? <span className="pf-prose"><Lines text={c.body} /></span> : null}
                </span>
                <span className="pf-feed__meta pf-sub">{fmtDate(c.created_at)}</span>
              </li>
            ))}
          </ul>
        ) : <div className="pf-empty pf-empty--sm">Noch keine Kommentare.</div>}
      </Card>
      <Card
        title="Antworten"
        hint="Der Kunde sieht Kommentar und Status im eigenen Feedback-Dialog, gezeichnet mit deinem Namen und „(Solutec)“, und bekommt eine Mail."
        foot={(
          <>
            {nextStatuses(r.type, r.status).map((s) => (
              <button key={s} type="button" className="pf-btn" disabled={busy} onClick={() => void setStatus(s)}>{STATUS[s]?.[0] ?? s}</button>
            ))}
            <button type="button" className="pf-btn pf-btn--primary" disabled={busy} onClick={() => void comment()}>Kommentieren</button>
          </>
        )}
      >
        <form className="pf-list" noValidate onSubmit={(e) => e.preventDefault()}>
          <textarea
            className="pf-input pf-input--prose pf-input--tall" name="body" aria-label="Kommentar"
            placeholder="Kommentar (optional beim Statuswechsel)" value={text} onChange={(e) => setText(e.target.value)}
          />
          <span className="pf-field__error" role="alert">{error}</span>
        </form>
      </Card>
    </>
  );
}
